import {
	GridDimension,
	GridPosition,
	GridArea
} from "client/Api/Workspace/common";
import { ArgumentNotSuppliedError } from "client_legacy/ndaw/errors";


class GridSpaceTracker {
	private readonly _columnSpan: number;
	private _rows: GridRowSpaceTracker[];
	private _lastFillPosition: GridPosition;

	constructor(columnSpan: number) {
		this._columnSpan = columnSpan;
		this._rows = [];
		this._lastFillPosition = { column: 0, row: 0 };
	}

	public resetFillingPosition() {
		this._lastFillPosition = { column: 0, row: 0 };
	}

	public commitFirstAvailablePosition(dimension: GridDimension): GridPosition {
		if (!dimension) {
			throw new ArgumentNotSuppliedError("dimension");
		}

		const { columnSpan } = dimension;
		if (columnSpan > this._columnSpan) {
			throw new Error("Exceeds column span of the grid");
		}

		for (let currentRowIndex = this._lastFillPosition.row; ; currentRowIndex++) {
			const row: GridRowSpaceTracker = this.row(currentRowIndex);
			if (row.largestConsecutiveFreeSpace < columnSpan || row.firstFreeCell === null) {
				continue;
			}

			const startingColumn = this._lastFillPosition.row === currentRowIndex
				? this._lastFillPosition.column
				: 0;

			for (let currentColumnIndex = Math.max(row.firstFreeCell, startingColumn); currentColumnIndex < this._columnSpan; currentColumnIndex++) {
				const remainingColumnsInCurrentRow = this._columnSpan - currentColumnIndex;
				const canStillFitInCurrentRow = remainingColumnsInCurrentRow >= columnSpan;

				if (!canStillFitInCurrentRow) {
					break;
				}

				const position: GridPosition = {
					column: currentColumnIndex,
					row: currentRowIndex
				};

				const area: GridArea = {
					position: position,
					dimension: dimension
				};


				const areaIsFree: boolean = this.isAreaFree(area);
				// ┏━━━━┓
				// ┃ a  ┃  (x)
				// ┗━━━━┛
				// ┏━━━━━━━━━━┓
				// ┃          ┃
				// ┃    b     ┃
				// ┃          ┃
				// ┗━━━━━━━━━━┛
				// if we try to place 2x2 at(x), it wouldn't fit without intersections b
				if (areaIsFree) {
					this.commitArea(area);
					this._lastFillPosition = area.position;
					return area.position;
				}
			}
		}
	}

	private row(index: number): GridRowSpaceTracker {
		if (typeof index === "undefined" || index === null) {
			throw new ArgumentNotSuppliedError("index");
		}

		let row: GridRowSpaceTracker = this._rows[index];
		if (!row) {
			row = new GridRowSpaceTracker(this._columnSpan);
			this._rows[index] = row;
		}
		return row;
	}

	private isAreaFree(area: GridArea): boolean {
		for (let row = area.position.row; row < area.position.row + area.dimension.rowSpan; row++) {
			const areCellsFree: boolean = this.row(row).areCellsFree(area.position.column, area.dimension.columnSpan);
			if (!areCellsFree) {
				return false;
			}
		}

		return true;
	}

	private commitArea(area: GridArea) {
		for (let row = area.position.row; row < area.position.row + area.dimension.rowSpan; row++) {
			this.row(row).commitCells(area.position.column, area.dimension.columnSpan);
		}
	}
}

class GridRowSpaceTracker {

	private _cells: boolean[];
	private _largestConsecutiveFreeSpace: number;
	private _firstFreeCell: number | null;

	public get cells(): ReadonlyArray<boolean> {
		return this._cells;
	}

	public get largestConsecutiveFreeSpace(): number {
		return this._largestConsecutiveFreeSpace;
	}

	public get firstFreeCell(): number | null {
		return this._firstFreeCell;
	}

	constructor(columnSpan: number) {
		this._cells = [];
		this._firstFreeCell = 0;
		for (let i = 0; i < columnSpan; i++) {
			this._cells[i] = false;
		}
		this._largestConsecutiveFreeSpace = columnSpan;
	}

	public areCellsFree(column: number, span: number): boolean {
		if (column + span > this._cells.length) {
			return false;
		}

		for (let i = column; i < column + span; i++) {
			if (this.isCellFilled(i)) {
				return false;
			}
		}

		return true;
	}

	public commitCells(column: number, span: number) {
		if (column + span > this._cells.length) {
			throw new Error("Does not fit the row");
		}

		for (let i = column; i < column + span; i++) {
			if (this.isCellFilled(i)) {
				throw new Error("Space was already committed");
			}

			this._cells[i] = true;
		}

		this.updateState();
	}

	private updateState() {
		let largestConsecutiveFreeSpace: number = 0;
		let currentFreeSpace: number = 0;
		let foundFreeCell: boolean = false;
		for (let i = 0; i < this._cells.length; i++) {
			if (this.isCellFilled(i)) {
				if (currentFreeSpace > largestConsecutiveFreeSpace) {
					largestConsecutiveFreeSpace = currentFreeSpace;
				}
				currentFreeSpace = 0;
			} else {
				if (!foundFreeCell) {
					this._firstFreeCell = i;
					foundFreeCell = true;
				}
				currentFreeSpace++;
			}
		}

		if (currentFreeSpace > largestConsecutiveFreeSpace) {
			largestConsecutiveFreeSpace = currentFreeSpace;
		}

		if (!foundFreeCell) {
			this._firstFreeCell = null;
		}

		this._largestConsecutiveFreeSpace = largestConsecutiveFreeSpace;
	}

	private isCellFilled(column: number) {
		if (column >= this._cells.length) {
			throw new Error("Out of bound");
		}
		return this._cells[column];
	}
}

export {
	GridSpaceTracker
};
